import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Box,
  Alert,
  Typography,
  Grid,
  Paper } from
"@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import C1ListPanel from "app/c1component/C1ListPanel";
import { SimpleCard } from "app/components";
import C1FormButtons from "app/c1component/C1FormButtons";
import axios from "app/utils/axios.js";
import apiPaths from "app/utils/apiPaths";
import { MatxLoading } from "app/components";
import LocalStorageService from "app/service/localStorageService";

const PasswordChange = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();


  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmNewPassword: false
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleToggleShowPassword = (field) => () => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: !prevValues[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordValidationError = validateNewPassword(values.newPassword);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }

    if (values.newPassword !== values.confirmNewPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(apiPaths.UPDATE_PASSWORD, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      setSuccess(response.data.message || "Password changed successfully!");

      setValues({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
        showCurrentPassword: false,
        showNewPassword: false,
        showConfirmNewPassword: false
      });

      const localStorageService = LocalStorageService.getService();
      await axios
      .get(apiPaths.LOGOUT)
      .catch((logoutError) => console.error(logoutError));
      localStorageService.clearToken();
      setTimeout(() => {
        navigate("/session/signin");
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Failed to change password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const props = {
    showSave: false,
    showBack: false
  };

  const validateNewPassword = (password) => {
    const minLength = password.length >= 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialOrNumber = /[\d\s!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!minLength) return "Password must be at least 8 characters long.";
    if (!hasLowercase)
    return "Password must include at least one lowercase letter.";
    if (!hasUppercase)
    return "Password must include at least one uppercase letter.";
    if (!hasSpecialOrNumber)
    return "Password must include at least one number, symbol, or whitespace character.";

    return null;
  };










  return (
    <C1ListPanel routeSegments={[{ name: "Change Password" }]}>
      <Stack spacing={3}>
        <C1FormButtons props={props} onSubmit={handleSubmit} />
        <SimpleCard title="Password Change">
          {loading && <MatxLoading />}
          <Grid container spacing={2}>
            {/* Password Change Form */}
            <Grid item xs={12} md={8}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ maxWidth: 400, mx: "auto", mt: 4 }}>

                {error &&
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                }
                {success &&
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                }
                <TextField
                  label="Current Password"
                  type={values.showCurrentPassword ? "text" : "password"}
                  value={values.currentPassword}
                  onChange={handleChange("currentPassword")}
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment:
                    <InputAdornment position="end">
                        <IconButton
                        onClick={handleToggleShowPassword(
                          "showCurrentPassword"
                        )}
                        edge="end">

                          {values.showCurrentPassword ?
                        <Visibility /> :

                        <VisibilityOff />
                        }
                        </IconButton>
                      </InputAdornment>

                  }} />

                <TextField
                  label="New Password"
                  type={values.showNewPassword ? "text" : "password"}
                  value={values.newPassword}
                  onChange={handleChange("newPassword")}
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment:
                    <InputAdornment position="end">
                        <IconButton
                        onClick={handleToggleShowPassword("showNewPassword")}
                        edge="end">

                          {values.showNewPassword ?
                        <Visibility /> :

                        <VisibilityOff />
                        }
                        </IconButton>
                      </InputAdornment>

                  }} />

                <TextField
                  label="Re-enter New Password"
                  type={values.showConfirmNewPassword ? "text" : "password"}
                  value={values.confirmNewPassword}
                  onChange={handleChange("confirmNewPassword")}
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment:
                    <InputAdornment position="end">
                        <IconButton
                        onClick={handleToggleShowPassword(
                          "showConfirmNewPassword"
                        )}
                        edge="end">

                          {values.showConfirmNewPassword ?
                        <Visibility /> :

                        <VisibilityOff />
                        }
                        </IconButton>
                      </InputAdornment>

                  }} />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={loading}>

                  {loading ? "Changing Password..." : "Change Password"}
                </Button>
              </Box>
            </Grid>

            {/* Password Requirements */}
            <Grid item xs={12} md={4} sx={{ margin: "auto" }}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Password Requirements
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Ensure that these requirements are met:
                </Typography>
                <ul>
                  <li>Minimum 8 characters long - the more, the better</li>
                  <li>At least one lowercase character</li>
                  <li>At least one uppercase character</li>
                  <li>At least one number, symbol, or whitespace character</li>
                </ul>
              </Paper>
            </Grid>
          </Grid>
        </SimpleCard>
        <C1FormButtons props={props} onSubmit={handleSubmit} />
      </Stack>
    </C1ListPanel>);

};

export default PasswordChange;